import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useSocketStore } from '../../store/socketStore';
import { Loader2 } from 'lucide-react';

// Routes that don't require subscription
const SUBSCRIPTION_EXEMPT_ROUTES = ['/select-plan', '/checkout'];

export function ProtectedRoute({ children }) {
  const location = useLocation();
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const { connect, disconnect } = useSocketStore();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Skip check if user data passed from AuthCallback
    if (location.state?.user) {
      setChecking(false);
      return;
    }

    const verify = async () => {
      await checkAuth();
      setChecking(false);
    };
    
    verify();
  }, [checkAuth, location.state]);

  // Connect to WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.tenant_id) {
      connect(user.tenant_id);
    }

    return () => {
      // Don't disconnect on unmount - let the socket persist across route changes
    };
  }, [isAuthenticated, user?.tenant_id, connect]);

  if (checking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if current route is exempt from subscription check
  const isExemptRoute = SUBSCRIPTION_EXEMPT_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  // Check subscription status (skip for superadmin and exempt routes)
  if (!isExemptRoute && user?.role !== 'superadmin' && !user?.has_subscription) {
    return <Navigate to="/select-plan" replace />;
  }

  return children;
}
